import { useCallback, useEffect, useMemo, useState } from 'react';
import { diveSiteCatalog as seededDiveSiteCatalog } from '../data/diveSites';
import { importedDiveSiteCatalog } from '../data/diveSitesImported';
import { expansionDiveSiteCatalog } from '../data/diveSitesExpansion';
import { communityDiveSiteService } from '../services/communityDiveSiteService';
import type { CreateCommunityDiveSiteRequest, DiveSite, UpdateCommunityDiveSiteRequest } from '../types/DiveSite';

const seededCatalog: DiveSite[] = [...seededDiveSiteCatalog, ...importedDiveSiteCatalog, ...expansionDiveSiteCatalog]
  .map((site) => ({
    ...site,
    source: 'seed' as const,
  }))
  .sort((left, right) => left.name.localeCompare(right.name));

const normalizeCommunitySite = (site: DiveSite): DiveSite => ({
  ...site,
  id: site.id || site.siteId || site.slug,
  siteId: site.siteId || site.id,
  aliases: site.aliases ?? [],
  highlights: site.highlights ?? [],
  tags: site.tags ?? [],
  source: 'community',
});

const mergeCatalogs = (communitySites: DiveSite[]): DiveSite[] => {
  const merged = new Map<string, DiveSite>();

  seededCatalog.forEach((site) => {
    merged.set(site.slug, site);
  });

  communitySites.forEach((site) => {
    merged.set(site.slug, normalizeCommunitySite(site));
  });

  return Array.from(merged.values());
};

export const useDiveSiteCatalog = () => {
  const [communitySites, setCommunitySites] = useState<DiveSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCommunitySites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const sites = await communityDiveSiteService.getCommunityDiveSites();
      setCommunitySites(sites.map(normalizeCommunitySite));
    } catch (err) {
      console.error('Failed to load community dive sites:', err);
      setError('Unable to load community-added dive sites right now.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCommunitySites();
  }, [loadCommunitySites]);

  const addCommunitySite = useCallback(async (request: CreateCommunityDiveSiteRequest) => {
    setSaving(true);
    setError(null);

    try {
      const site = normalizeCommunitySite(await communityDiveSiteService.createCommunityDiveSite(request));
      setCommunitySites((previousSites) => {
        const nextSites = previousSites.filter((previousSite) => previousSite.slug !== site.slug);
        return [...nextSites, site].sort((left, right) => left.name.localeCompare(right.name));
      });
      return site;
    } catch (err) {
      console.error('Failed to create community dive site:', err);
      const message = err instanceof Error ? err.message : 'Failed to add dive site';
      setError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  const updateCommunitySite = useCallback(async (request: UpdateCommunityDiveSiteRequest) => {
    setSaving(true);
    setError(null);

    try {
      const site = normalizeCommunitySite(await communityDiveSiteService.updateCommunityDiveSite(request));
      setCommunitySites((previousSites) => {
        const nextSites = previousSites.filter((previousSite) => previousSite.id !== site.id);
        return [...nextSites, site].sort((left, right) => left.name.localeCompare(right.name));
      });
      return site;
    } catch (err) {
      console.error('Failed to update community dive site:', err);
      const message = err instanceof Error ? err.message : 'Failed to update dive site';
      setError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  const allSites = useMemo(() => mergeCatalogs(communitySites), [communitySites]);

  return {
    allSites,
    communitySites,
    loading,
    saving,
    error,
    reload: loadCommunitySites,
    addCommunitySite,
    updateCommunitySite,
  };
};